import { Component, OnInit, HostBinding, ChangeDetectorRef, ViewChildren, QueryList } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, ValidatorFn, AbstractControl, UntypedFormArray, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';

import * as _ from 'lodash';

import { BrandService } from 'shared/brand/brand.service';
import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';
import { CollapseGroupComponent } from 'shared/collapse/collapse-group/collapse-group.component';
import { CollapseComponent } from 'shared/collapse/collapse/collapse.component';
import { DetailsControlComponent } from 'shared/details-page/details-control/details-control.component';
import { createScoreFieldRequiredValidation } from 'shared/validation/scorecard-field-validation';

import { SegmentFieldItem } from 'utils/segment-field-item.interface';
import { BaseSegmentComponent } from '../base/_base-segment.component';


@Component({
  template: '',
})
export class BaseSegmentListComponent extends BaseSegmentComponent implements OnInit {
  rowData: any[];
  contentData: any[];
  segmentData: any;
  controlClassPrefix: string = 'control-row-';

  @ViewChildren(CollapseGroupComponent)
  collapseGroups: QueryList<CollapseGroupComponent>;

  @ViewChildren(CollapseComponent)
  collapseComponents: QueryList<CollapseComponent>;

  // the array of subscriptons on form controls, which are not displayed
  // to emit status changes or populated automatically
  // hiddenControlsValueChangeSubsr: any[] = [];

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
    protected brandService: BrandService,
    protected fb: UntypedFormBuilder,
    protected sanitizer: DomSanitizer,
  ) {
    super(ref, scorecardConnectorService, brandService, fb);
  }

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get formArray(): UntypedFormArray {
    return this.segmentForm.get(this.segmentName) as UntypedFormArray;
  }

  get itemDetailData(): Array<any> {
    return _.get(this.itemDetail, this.segmentName);
  }

  get isEmpty(): boolean {
    return !_.get(this.itemDetailData, 'length');
  }

  get hiddenFields(): any[] {
    return _.filter(this.segmentData, (segmentItem: SegmentFieldItem) => {
        // @ts-ignore
      return !_.find(this.rowData, {fieldName: segmentItem.field_map}) &&
          // @ts-ignore
        !_.some(_.map(this.contentData), (contentDataItem: any) => {
          // it's fieldName is the same or it contains it in combined fields
          return _.find(contentDataItem, {fieldName: segmentItem.field_map}) ||
            _.indexOf(contentDataItem.combined, segmentItem.field_map) >= 0;
        });
    });
  }

  /*******************************************
  * COMPONENT SETTERS
  *******************************************/

  set itemDetailData(data: Array<any>) {
    _.set(this.itemDetail, this.segmentName, data);
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    super.ngOnInit();
    this.segmentForm = this.fb.group({
      [this.segmentName]: this.fb.array([])
    });
    if (this.itemDetail) {
      this.handleItemDetail(this.itemDetail);
    }
    this.runAnimation('show');
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public getField(fieldMap: string): string {
      // @ts-ignore
    return _.find(this.segmentData, {field_map: fieldMap});
  }

  public getFieldTitle(fieldMap: string): string {
    const field: any = this.getField(fieldMap);
    return field ? field.title : '';
  }

  public getFieldLookup(fieldMap: string): any[] {
    const field: any = this.getField(fieldMap);
    return field ? field.lookup : null;
  }

  /*
    * Retuns validation object according on field's score rule
   */
  public getItemValidation(item: any): any {
    const rule = this.getItemValidationRule(item);
    return this.getItemValidationByRuleName(item, rule);
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onCollapseClick(index: number) {
    this.collapseRow(index);
  }

  onRowRemove(formGroup: UntypedFormGroup) {
    const index = this.formArray.controls.indexOf(formGroup);
    this.formArray.removeAt(index);
    this.itemDetailData.splice(index, 1);
    if (this.segmentScores) {
      this.segmentScores.splice(index, 1);
      this.scorecardConnectorService.detectChangesInScorecard.emit();
    }
    this.formArray.markAsDirty();
  }

  onRowAdd() {
    const item = this.createItem();
    this.itemDetailData.push(item);
    const fg = this.createItemFG(item);
    this.formArray.push(fg);
    this.formArray.markAsDirty();
    if (this.segmentScores) {
      this.scorecardConnectorService.controlAdded.emit();
      this.scorecardConnectorService.detectChangesInScorecard.emit();
    }
    this.ref.markForCheck();
    _.delay(() => this.collapseRow(this.itemDetailData.length - 1), 200);
  }

  onItemControlStatusChange(status: any, itemName: string, index: number) {
    if (!this.segmentScores) { return; }

    const item = this.getField(itemName);
    this.scorecardConnectorService.controlStatusChanged.emit({
      status, index, item,
    });
  }

  onItemControlPopulatedChange(populated: any, itemName: string, index: number) {
    if (!this.segmentScores) { return; }

    const item = this.getField(itemName);
    this.scorecardConnectorService.controlPopulatedChanged.emit({
      populated, index, item,
    });
  }

  onItemControlValueChange(value: string, formGroup: UntypedFormGroup, fieldName: string) {
    this.updateFormValueAndValidity(formGroup);
  }

  onItemControlSelectChange(formGroup: UntypedFormGroup) {
    this.updateFormValueAndValidity(formGroup);
  }


  onItemControlFocus(fieldMap: string, index: number, valueIndex: number) {
    if (!this.segmentScores) { return; }

    this.scorecardConnectorService.controlFocused.emit({
      index,
      valueIndex: (valueIndex >= 0 ? valueIndex : null),
      item: this.getField(fieldMap),
    });
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  /*
    * Define in the child
    * Handle updating item details data here
  */
  protected handleItemDetail(itemDetail: any): void {
    super.handleItemDetail(itemDetail);
    if (!this.itemDetailData) {
      this.itemDetailData = [];
    }
  }

  /*
    * Define in the child
    * Handle updating item details data here
  */
  protected handleAllDataUpdate(): void {
    super.handleAllDataUpdate();
    if (!this.itemDetailData) {
      this.itemDetailData = [];
    }
  }

  protected getTitle(name: string): string {
    // find title in rowData
      // @ts-ignore
    const colData = _.find(this.rowData, {fieldName: name});
      // @ts-ignore
    if (_.get(colData, 'title')) { return colData.title; }

    // find title in segmentData
    const item: any = _.find(this.segmentData, {field_map: name});
    return item ? item.title : '';
  }

  protected getControlValidators(item: SegmentFieldItem): Array<ValidatorFn> {
    const validationRule = this.getItemValidationRule(item);
    const validation = this.getItemValidationByRuleName(item, validationRule);
    validation.conditionals = null;
    validation.required = this.getItemRequiredRule(item);

    // find all conditionals where item is in 'required' -
    // we need to find what fields it depends on
    const conditionals = this.getRequiredConditionalsForField(item.item_name); // from scorecard item, 'custom_validation_rules'

    if (conditionals) {
      validation.required = null;
      validation.conditionals = createScoreFieldRequiredValidation(conditionals);
    }

    return this.initValidators(validation, this);
  }

  /*
    Get validation from /api/scorecard data
  */
  protected getItemValidationRule(item: SegmentFieldItem) {
    const score = this.getScorecardField(item.item_name);
    return _.get(score, 'validation_rule', {}); // pies65, pies64, etc
  }

  /*
    Get required from /api/scorecard data
  */
  protected getItemRequiredRule(item: SegmentFieldItem) {
    const score = this.getScorecardField(item.item_name);
    return !!_.get(score, 'required');
  }

  /*
    * Replaced parent
   */
  protected getScorecardField(name: string): any {
    return _.find(this.scorecardFields, (scorecardField: any) => {
      return (scorecardField.path.indexOf(`${this.segmentName}.%i.${name}`) === 0);
    });
  }

  protected getScoreItem(item: SegmentFieldItem, index: number|string): any {
    return _.get(this.segmentScores, `${index}.${item.item_name}`);
  }

  protected getScoreIndexItem(item: SegmentFieldItem, index: number|string, propertyIndex: number): any {
    return _.get(this.segmentScores, `${index}.${item.item_name}[${propertyIndex}]`);
  }



  // replace this method in child
  // This method handle every row - add extra fields or modify existing
  // before creating form array
  protected handleDataItem(item: any) {
    return {...item};
  }

  protected collapseRow(index: number, collapse?: boolean) {
    // pass -1 to collapse all
    const collapseGroupsArr: any = this.collapseGroups.toArray();

    if (!_.get(collapseGroupsArr, 'length') || !_.get(collapseGroupsArr, `[${index}]`)) { return; }
    if (_.isNil(collapse)) { collapse = !collapseGroupsArr[index].collapsed; }
    collapseGroupsArr[index].setCollapsed(collapse);

    this.scorecardConnectorService.tableRowCollapsed.emit({
      index,
      collapsed: collapse,
    });

    this.ref.markForCheck();
  }

  /*
    * Define in the child
    * Used to focus on control
  */
  protected handleScoreDetailsExpand($event: any): void {
    if (_.has($event, 'index')) {
      if (!_.get($event, 'item')) {
        /* if it's collapsable row */
        this.collapseRow($event.index, !($event.expanded));
      } else {
        if (!this.editMode) { return; }
        this.collapseRow($event.index, !($event.expanded));

        const fieldMap = $event.item.field_map;
        const controls = this.getRowControls($event.index, fieldMap);

        _.defer(() => {
          if (_.size(controls)) { controls[0].focusControl(fieldMap); }
        });

      }
    }
  }

  /*
    * Define in the child
    * Used to create item for data array
  */
  protected createItem() {
    const item = {};
    _.forEach(this.segmentData, (segment: any) => {
      _.set(item, segment.item_name, null);
    });
    return item;
  }


  protected getTypeahead(formGroup: UntypedFormGroup, fieldName: string) {
    const dataSource = formGroup.get(`${fieldName}_dataSource`);
    if (dataSource) {
      return dataSource.value;
    }
    return null;
  }

  protected getTypeaheadOptionField(fieldName: string) {
    const field = this.getField(fieldName);
    if (field) {
      return _.get(field, 'api_lookup.item_name');
    }
    return null;
  }

  /*
    * Define in the child
    * Used to update controls validation after scorecard changes
  */
  protected updateControlsValidation(): void {
    _.each(this.itemDetailData, (itemDetailItem: any, index: number) => {
      _.each(this.segmentData, (item: SegmentFieldItem) => {
        const formControl: any = this.formArray.at(index).get(item.field_map);
        if (!formControl) { return; }
        const validators: any = this.getControlValidators(item);
        if (formControl.controls) {
          // FormArray
          _.each(formControl.controls, (control: AbstractControl) => {
            this.updateControlValidators(control, validators);
          });
        } else {
          // FormControl
          this.updateControlValidators(formControl, validators);
        }
      });
    });
  }

  protected getMaxLength(fieldName: string, index: number) {
    const item = this.getFieldByMap(fieldName, this.segmentData);
    const validation = this.getItemValidation(item);
    return validation ? validation.maxLength : null;
  }

  // check whether the fiels is in score or not
  // field is from table-data
  protected hasItemInScores(field: any): boolean {
    const item = this.getFieldByMap(field.fieldName, this.segmentData);
    if (!item) { return false; }
    return _.has(this.segmentScores, item.item_name);
  }

  /*
    * Used to highlight control
  */
  // redefine parent method
  protected handleScorecardItemHover($event: any): void {
    const control = this.findControlViaData($event);
    if (control) { control.highlightControl(); }
  }

  /*
    * Used by handleScorecardItemHover, handleScorecardItemBlur
    * Finds control using data from event
    * Define in the child
    * data = {
    *   item: segment item,
    *   index: row index
    * }
   */
   // redefine parent method
  protected findControlViaData(data: any): DetailsControlComponent {
    if (! _.has(data, 'item') || !_.has(data, 'index')) {
      return null;
    }
    return this.findControlByFieldMap(data.item.field_map, data.index);
  }

  protected findControlByFieldMap(fieldMap: string, index?: number): DetailsControlComponent|undefined {
    return _.get(this.getRowControls(index, fieldMap), '[0]');
  }

  protected getInvalidDot(fieldMap: string, formGroup: UntypedFormGroup, index: number) {
    const control = _.get(formGroup, `controls.${fieldMap}`);
    if (control.invalid) {
      return   this.sanitizer.bypassSecurityTrustHtml(`
        <div class="circle-icon size-small cell-invalid-dot circle-red"
        data-ghost="cell-invalid-dot-${fieldMap}-${index}"></div>
      `);
    } else {
      return null;
    }
  }

  /*
    Emits conditional statuses of fields
    Needs to be inherited by child
  */
  protected emitConditionalStatuses() {
    console.log('emitConditionalStatuses is not defined!');
  }


}

